// Bottom sheet on phone, side panel on PC (≥ 900px). A modal <dialog>: focus is trapped, Esc
// closes, the page behind is inert, and focus returns to what opened it. Appears instantly (only
// two moments animate in this app); on the phone it follows a finger dragging it down by its head
// only, once the finger has moved past START_SLOP, and on release closes (dragged far enough, or
// flicked) or settles back, at once. The close starts from where the finger left it: no jump back
// first. (iOS standalone has no Back gesture: dragging down and ✕ are the ways out.) The content never
// drags the sheet (2026-10-13, docs/next/fb-recording-sheet-closes.md: a tap that moved a little on a
// Kuuntele button closed the recording sheet), and a sheet offers its foot and its body's place to
// what it shows (components/SheetSlots.tsx: Voice Dock's pinned recorder).
// While the on-screen keyboard is up (iOS), the sheet stands above it (`--kb`, visualViewport).
//
// The margin's sheets (docs/design/SIVUPALSTA.md: word, line, quick settings) use `size="half"`
// (~55% of the screen) and `anchor`: the line the sheet is about is scrolled into the top 40%, so
// the sheet never covers it. Each of those is a history entry of its own (the margin context puts
// it in the URL); any other sheet can ask for one with `history` (Back closes it).
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useT } from '../i18n';
import { Icon } from './Icon';
import { SheetSlotsContext, type SheetSlots } from './SheetSlots';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Accent for the title rule, e.g. 'purple' for flag sheets. */
  tone?: 'default' | 'purple';
  /** 'half': ~55% of a phone's height (the margin's sheets); PC: a 380px side panel. Default: up to 88%, PC 440px. */
  size?: 'default' | 'half';
  /** Phone: the element the sheet is about, scrolled into the top 40% while the sheet is open. */
  anchor?: HTMLElement | null;
  /** Push a history entry while open: Back closes the sheet (the margin's sheets have one already). */
  history?: boolean;
  /** An extra class on the dialog (e.g. a sheet's own height on a short phone). */
  className?: string | undefined;
}

const PHONE = '(max-width: 899.98px)';
/** Drag distance (px) past which a released sheet closes. */
const DRAG_CLOSE = 80;
/** A flick: released moving down faster than this (px/ms) closes, however short the drag. */
const FLICK = 0.11;
/** A drag shorter than this (px) is no flick (a tap that wobbled). */
const SLOP = 8;
/** The finger must move this far down (px) before the sheet follows it at all: a tap is never a drag. */
const START_SLOP = 10;
/** The last movement older than this (ms) at release: the finger had stopped, no flick. */
const FLICK_STALE = 100;

interface Drag {
  /** The finger's y where the drag started. */
  y0: number;
  dy: number;
  lastY: number;
  lastT: number;
  /** Speed of the last movement, px/ms (down is positive). */
  v: number;
  /** The finger passed START_SLOP: the panel follows it. */
  started: boolean;
}

/** Height of the on-screen keyboard covering the bottom of the layout viewport (0 when zoomed). */
function keyboardHeight(): number {
  const vv = window.visualViewport;
  if (!vv || vv.scale > 1.01) return 0;
  const kb = Math.round(window.innerHeight - vv.height - vv.offsetTop);
  // a few pixels of browser chrome are no keyboard
  return kb > 60 ? kb : 0;
}

/** A same-app link the router would push (not a new tab, not a download, not elsewhere). */
function innerHref(e: MouseEvent): string | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>('a[href]') : null;
  if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  return url.pathname + url.search + url.hash;
}

/**
 * Scroll the page so `el` sits in the top 40% of the screen, above a half-height sheet (also the
 * margin's anchor when it is restored from the URL: margin/context.tsx).
 */
export function keepInTopBand(el: HTMLElement): void {
  const main = document.getElementById('main');
  // only a line in the page (not the top bar's button that opened quick settings)
  if (!main || !main.contains(el)) return;
  const vh = window.innerHeight;
  const r = el.getBoundingClientRect();
  const m = main.getBoundingClientRect();
  const top = Math.max(m.top + 8, vh * 0.12);
  if (r.top >= m.top && r.bottom <= vh * 0.4) return;
  main.scrollTop += r.top - top;
}

/**
 * Back closes a sheet opened with `history`: one entry pushed while it is open. A component of its
 * own, so a sheet without `history` needs no router (unit tests render sheets bare). A link inside
 * the sheet replaces that entry instead of pushing past it: Back from where it leads goes to the
 * page under the sheet, not to a sheet that is no longer there.
 */
function SheetHistory({ open, onClose, dialog }: { open: boolean; onClose: () => void; dialog: RefObject<HTMLDialogElement | null> }): null {
  const navigate = useNavigate();
  const loc = useLocation();
  const key = useId();
  const mine = !!loc.state && typeof loc.state === 'object' && (loc.state as Record<string, unknown>).sheet === key;
  /** 'pushed': the entry was asked for; 'in': it is the current one. */
  const phase = useRef<'idle' | 'pushed' | 'in'>('idle');
  useEffect(() => {
    if (open && phase.current === 'idle') {
      phase.current = 'pushed';
      const st = loc.state && typeof loc.state === 'object' ? (loc.state as Record<string, unknown>) : {};
      void navigate({ pathname: loc.pathname, search: loc.search, hash: loc.hash }, { state: { ...st, sheet: key } });
      return;
    }
    if (open && mine) {
      phase.current = 'in';
      return;
    }
    if (open && phase.current === 'in' && !mine) {
      // Back (or a link) left the entry while the sheet was open: close it.
      phase.current = 'idle';
      onClose();
      return;
    }
    if (!open && phase.current !== 'idle') {
      phase.current = 'idle';
      if (mine) void navigate(-1);
    }
  }, [open, mine, navigate, loc, key, onClose]);

  // a link inside the open sheet: it takes the sheet's entry (capture: before the router's own click)
  useEffect(() => {
    const d = dialog.current;
    if (!open || !mine || !d) return;
    const onClick = (e: MouseEvent) => {
      const to = innerHref(e);
      if (!to) return;
      e.preventDefault();
      phase.current = 'idle';
      void navigate(to, { replace: true });
    };
    d.addEventListener('click', onClick, true);
    return () => d.removeEventListener('click', onClick, true);
  }, [open, mine, dialog, navigate]);
  return null;
}

function startDrag(y: number): Drag {
  return { y0: y, dy: 0, lastY: y, lastT: performance.now(), v: 0, started: false };
}

/** The panel follows the finger (down only); the speed of the last movement is kept for a flick. */
function followDrag(d: Drag | null, panel: HTMLElement | null, y: number): void {
  if (!d || !panel) return;
  const now = performance.now();
  if (now > d.lastT) d.v = (y - d.lastY) / (now - d.lastT);
  d.lastY = y;
  d.lastT = now;
  d.dy = Math.max(0, y - d.y0);
  if (!d.started && d.dy < START_SLOP) return;
  d.started = true;
  panel.style.transform = d.dy ? `translateY(${d.dy}px)` : '';
}

export function Sheet({ open, onClose, title, children, tone = 'default', size = 'default', anchor, history = false, className }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const drag = useRef<Drag | null>(null);
  const dragPointer = useRef<number | null>(null);
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const t = useT();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      // a drag that closed it last time left the panel where the finger let go
      if (panel.current) panel.current.style.transform = '';
      opener.current = document.activeElement;
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
      // showModal focuses the first control, the ✕, and draws its focus ring on every sheet: focus
      // the title instead (no ring; a screen reader starts from it). A control the content focused
      // itself keeps its focus.
      const a = document.activeElement;
      if (!a || a === closeRef.current || a === d || a === document.body) titleRef.current?.focus({ preventScroll: true });
    } else if (!open && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
      // Focus back to what opened the sheet (the browser does it too; not when it is gone).
      const back = opener.current;
      opener.current = null;
      if (back instanceof HTMLElement && back.isConnected && document.activeElement === document.body) back.focus({ preventScroll: true });
    }
  }, [open]);

  // The line the sheet is about stays visible above it (phone, half-height sheets).
  useLayoutEffect(() => {
    if (!open || !anchor || size !== 'half' || !window.matchMedia?.(PHONE).matches) return;
    document.documentElement.classList.add('has-half-sheet');
    keepInTopBand(anchor);
    return () => document.documentElement.classList.remove('has-half-sheet');
  }, [open, anchor, size]);

  // While open: the on-screen keyboard's height (iOS keeps the layout viewport; the sheet would
  // sit behind the keyboard). CSS lifts the sheet by it and shortens the panel (components.css).
  useEffect(() => {
    const d = ref.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!open || !d || !vv) return;
    const update = () => d.style.setProperty('--kb', `${keyboardHeight()}px`);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      d.style.removeProperty('--kb');
    };
  }, [open]);

  // Phone: drag down to close; the panel follows the finger, nothing animates.
  const latestClose = useRef(onClose);
  useLayoutEffect(() => {
    latestClose.current = onClose;
  });
  /** Release: close from where it is (no snap back first), or settle back; a cancel settles back. */
  const release = useCallback((cancel: boolean) => {
    const d = drag.current;
    drag.current = null;
    dragPointer.current = null;
    if (!d) return;
    const flick = d.dy > SLOP && d.v > FLICK && performance.now() - d.lastT < FLICK_STALE;
    if (!cancel && d.started && (d.dy > DRAG_CLOSE || flick)) {
      latestClose.current();
      // the owner kept it open after all: settle back (the open effect clears it otherwise)
      window.setTimeout(() => {
        if (ref.current?.open && !drag.current && panel.current) panel.current.style.transform = '';
      }, 400);
      return;
    }
    if (panel.current) panel.current.style.transform = '';
  }, []);

  // by the head: pointer events (the head takes no browser gesture: touch-action none)
  const onDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' || !window.matchMedia?.(PHONE).matches || drag.current) return;
    if ((e.target as Element).closest('button')) return;
    dragPointer.current = e.pointerId;
    drag.current = startDrag(e.clientY);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (dragPointer.current === e.pointerId) followDrag(drag.current, panel.current, e.clientY);
  };
  const onUp = (e: ReactPointerEvent<HTMLElement>) => {
    if (dragPointer.current === e.pointerId) release(false);
  };
  const onCancel = (e: ReactPointerEvent<HTMLElement>) => {
    if (dragPointer.current === e.pointerId) release(true);
  };

  // the foot and the body's place for what the sheet shows (SheetSlots.tsx)
  const [dockEl, setDockEl] = useState<HTMLDivElement | null>(null);
  const [swapEl, setSwapEl] = useState<HTMLDivElement | null>(null);
  const [docks, setDocks] = useState(0);
  const [swaps, setSwaps] = useState(0);
  const slots = useMemo<SheetSlots>(
    () => ({
      dock: dockEl,
      swap: swapEl,
      holdDock: () => {
        setDocks((n) => n + 1);
        return () => setDocks((n) => n - 1);
      },
      holdSwap: () => {
        setSwaps((n) => n + 1);
        return () => setSwaps((n) => n - 1);
      },
    }),
    [dockEl, swapEl],
  );

  return (
    <dialog
      ref={ref}
      className={`sheet tone-${tone}${size === 'half' ? ' is-half' : ''}${className ? ` ${className}` : ''}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Click on the backdrop (the dialog box itself, outside the panel) closes.
        if (e.target === ref.current) onClose();
      }}
    >
      {history ? <SheetHistory open={open} onClose={onClose} dialog={ref} /> : null}
      <div className={`sheet-panel${docks > 0 ? ' has-dock' : ''}${swaps > 0 ? ' is-swapped' : ''}`} ref={panel}>
        <header
          className="sheet-head"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onCancel}
          onLostPointerCapture={onCancel}
        >
          <span className="sheet-grip" aria-hidden="true" />
          <h2 id={titleId} ref={titleRef} className="sheet-title" tabIndex={-1}>
            {title}
          </h2>
          <button ref={closeRef} type="button" className="icon-btn sheet-close" onClick={onClose} aria-label={t.sheet.close}>
            <Icon name="close" />
          </button>
        </header>
        <div className="sheet-swap" ref={setSwapEl} />
        <div className="sheet-body">
          <SheetSlotsContext.Provider value={slots}>{open ? children : null}</SheetSlotsContext.Provider>
        </div>
        <div className="sheet-dock" ref={setDockEl} />
      </div>
    </dialog>
  );
}
